import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, X, Copy, ArrowLeft, Loader2, Sparkles, GitCompareArrows, FileText, Share2, Lock } from "lucide-react";
import { Nav, Footer } from "@/components/Chrome";
import { NeuralField } from "@/components/NeuralField";
import { Gauge, scoreColor } from "@/components/Gauge";
import { PromptDiff } from "@/components/PromptDiff";
import { getAudit, setAuditShare } from "@/lib/api";

const USAGE_SOURCE_LABEL = {
  platform_baseline: "Platform baseline",
  user_setting: "Based on your usage setting",
  build_history: "Based on your build history",
};

// Isolated count-up so only this subtree re-renders (keeps sibling animations smooth)
const CountUpGauge = ({ target }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const duration = 1200;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return <Gauge score={val} />;
};

// Isolated typewriter (time-based so it completes even under frame throttling)
const Typewriter = ({ text }) => {
  const [typed, setTyped] = useState("");
  useEffect(() => {
    setTyped("");
    const total = 1600; // ms to fully type
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / total);
      const chars = Math.ceil(p * text.length);
      setTyped(text.slice(0, chars));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [text]);
  const done = typed.length >= text.length;
  return (
    <p className="font-mono text-sm leading-snug text-white">
      {typed}
      <span
        className="inline-block w-2 h-4 ml-0.5 bg-neon-cyan align-middle animate-pulse-glow"
        style={{ opacity: done ? 0 : 1 }}
      />
    </p>
  );
};

const Metric = ({ label, value, sub, color, testid }) => (
  <div className="rounded-xl p-4 bg-[#0b0d14]/60 border border-[var(--line)]" data-testid={testid}>
    <p className="font-mono text-[10px] tracking-[0.2em] text-[var(--muted)]">{label}</p>
    <p className="font-display font-bold text-3xl mt-1" style={{ color, textShadow: `0 0 16px ${color}55` }}>{value}</p>
    <p className="font-mono text-[10px] text-[var(--muted)] mt-0.5">{sub}</p>
  </div>
);

export default function Results() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [error, setError] = useState(false);
  const [view, setView] = useState("prompt"); // 'prompt' | 'diff'
  const [isPublic, setIsPublic] = useState(false);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    getAudit(id)
      .then((a) => {
        setAudit(a);
        setIsPublic(!!a.is_public);
      })
      .catch(() => setError(true));
  }, [id]);

  const copyImproved = () => {
    navigator.clipboard.writeText(audit.improved_prompt);
    toast.success("Improved prompt copied. Go paste it.");
  };

  const copyOriginal = () => {
    if (!audit.original_prompt) { toast.error("Original prompt wasn't stored for this audit."); return; }
    navigator.clipboard.writeText(audit.original_prompt);
    toast.success("Original prompt copied.");
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  };

  const copyLink = async () => {
    const ok = await copyToClipboard(window.location.href);
    toast.success(ok ? "Share link copied." : "Copy the link from your browser's address bar to share.");
  };

  const enableAndCopy = async () => {
    setSharing(true);
    try {
      if (!isPublic) {
        await setAuditShare(id, true);
        setIsPublic(true);
      }
      const ok = await copyToClipboard(window.location.href);
      toast.success(
        ok
          ? "Sharing on — link copied. Anyone with it can now view this result."
          : "Sharing on — copy the link from your browser's address bar to share."
      );
    } catch {
      toast.error("Couldn't update sharing. Please try again.");
    } finally {
      setSharing(false);
    }
  };

  const makePrivate = async () => {
    setSharing(true);
    try {
      await setAuditShare(id, false);
      setIsPublic(false);
      toast.success("Sharing off — this link is now private.");
    } catch {
      toast.error("Couldn't update sharing. Please try again.");
    } finally {
      setSharing(false);
    }
  };

  const Shell = ({ children }) => (
    <div className="relative text-[var(--text)] min-h-screen">
      <Nav />
      <div className="absolute top-0 inset-x-0 h-[420px] overflow-hidden pointer-events-none">
        <NeuralField className="w-full h-full opacity-70" density={55} />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#05060a]" />
      </div>
      {children}
    </div>
  );

  if (error) {
    return (
      <Shell>
        <div className="relative max-w-7xl mx-auto px-6 py-40 text-center">
          <p className="font-display text-3xl font-bold text-white">Audit not found.</p>
          <Link to="/" className="font-mono text-sm text-neon-cyan underline mt-4 inline-block">← Run a new audit</Link>
        </div>
      </Shell>
    );
  }
  if (!audit) {
    return (
      <Shell>
        <div className="relative max-w-7xl mx-auto px-6 py-40 flex items-center justify-center gap-3 font-mono text-sm text-neon-cyan">
          <Loader2 className="animate-spin" size={18} /> Compiling audit...
        </div>
      </Shell>
    );
  }

  const color = scoreColor(audit.score);
  const missing = audit.checklist.filter((c) => !c.present).length;

  return (
    <Shell>
      <main className="relative max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-8">
        <button
          data-testid="back-button"
          onClick={() => navigate("/")}
          className="font-mono text-xs flex items-center gap-1.5 mb-6 text-[var(--muted)] hover:text-neon-cyan transition-colors"
        >
          <ArrowLeft size={14} /> New audit
        </button>
        {audit.is_owner && (
          <span className="inline-flex items-center gap-3 mb-6 ml-4 align-middle" data-testid="share-controls">
            {isPublic ? (
              <>
                <button
                  data-testid="copy-share-link-button"
                  onClick={copyLink}
                  disabled={sharing}
                  className="font-mono text-xs inline-flex items-center gap-1.5 text-neon-cyan hover:underline disabled:opacity-50"
                >
                  <Share2 size={14} /> Copy share link
                </button>
                <button
                  data-testid="make-private-button"
                  onClick={makePrivate}
                  disabled={sharing}
                  className="font-mono text-xs inline-flex items-center gap-1.5 text-[var(--muted)] hover:text-neon-amber transition-colors disabled:opacity-50"
                >
                  <Lock size={13} /> Make private
                </button>
              </>
            ) : (
              <button
                data-testid="share-result-button"
                onClick={enableAndCopy}
                disabled={sharing}
                className="font-mono text-xs inline-flex items-center gap-1.5 text-[var(--muted)] hover:text-neon-cyan transition-colors disabled:opacity-50"
              >
                <Share2 size={14} /> Share result
              </button>
            )}
          </span>
        )}

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Gauge */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="md:col-span-4">
            <div className="glass neon-border rounded-2xl p-6 h-full flex flex-col">
              <p className="font-mono text-[11px] tracking-[0.28em] text-[var(--muted)] mb-4">
                PROMPT READINESS
              </p>
              <div className="flex-1 flex items-center justify-center py-2">
                <CountUpGauge target={audit.score} />
              </div>
              <div
                className="rounded-xl p-4 mt-3 glass"
                style={{ border: `1px solid ${color}55`, boxShadow: `inset 0 0 24px ${color}18` }}
                data-testid="verdict"
              >
                <Typewriter text={audit.verdict || ""} />
              </div>
              <p className="font-mono text-[11px] text-[var(--muted)] mt-3">
                Platform <span className="text-neon-cyan font-bold">{audit.platform}</span>
                {" · "}{missing}/{audit.checklist.length} checks flagged
              </p>
            </div>
          </motion.div>

          {/* Checklist */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
            className="md:col-span-8">
            <div className="glass rounded-2xl p-6 md:p-8 h-full">
              <h2 className="font-display text-2xl font-bold tracking-tight text-white mb-1">
                Diagnostic checklist
              </h2>
              <p className="font-mono text-xs text-[var(--muted)] mb-6">
                Each flag is a place the AI is likely to guess — increasing the risk of unnecessary rebuilds.
              </p>
              <ul className="space-y-3.5" data-testid="checklist">
                {audit.checklist.map((c, i) => (
                  <motion.li key={c.key}
                    initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + i * 0.06 }}
                    className="flex items-start gap-4 rounded-xl p-3.5 bg-[#0b0d14]/60 border border-[var(--line)]"
                    data-testid={`check-${c.key}`}>
                    <span
                      className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center"
                      style={{
                        color: c.present ? "#22e58a" : "#ff3b5c",
                        border: `1px solid ${c.present ? "#22e58a" : "#ff3b5c"}`,
                        boxShadow: `0 0 14px ${c.present ? "#22e58a" : "#ff3b5c"}45`,
                      }}
                    >
                      {c.present ? <Check size={16} strokeWidth={3} /> : <X size={16} strokeWidth={3} />}
                    </span>
                    <div>
                      <p className="font-display font-semibold text-base leading-tight text-white">
                        {c.label}
                        <span className="font-mono text-[10px] ml-2 tracking-[0.2em]"
                          style={{ color: c.present ? "#22e58a" : "#ff3b5c" }}>
                          {c.present ? "PRESENT" : "MISSING"}
                        </span>
                      </p>
                      <p className="font-mono text-sm text-[var(--muted)] mt-1 leading-relaxed">{c.reason}</p>
                    </div>
                  </motion.li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Savings estimate */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.18 }}
            className="md:col-span-12">
            <div className="glass rounded-2xl p-6 md:p-7 grid grid-cols-1 md:grid-cols-12 gap-6 items-center"
              style={{ border: "1px solid rgba(34,229,138,0.35)", boxShadow: "0 0 40px rgba(34,229,138,0.1)" }}
              data-testid="savings-estimate">
              <div className="md:col-span-3">
                <p className="font-mono text-[11px] tracking-[0.25em] text-[var(--muted)] mb-1">REBUILD LOOPS AVOIDED</p>
                <p className="font-display font-bold text-5xl" style={{ color: "#22e58a", textShadow: "0 0 20px rgba(34,229,138,0.5)" }} data-testid="loops-saved">
                  ~{audit.est_loops_saved}
                </p>
              </div>
              <div className="md:col-span-4">
                <p className="font-mono text-[11px] tracking-[0.25em] text-[var(--muted)] mb-1">ESTIMATED USAGE AVOIDED</p>
                {audit.usage_source ? (
                  <>
                    <p className="font-display font-bold text-5xl leading-none" style={{ color: "#22d3ee", textShadow: "0 0 20px rgba(34,211,238,0.5)" }} data-testid="usage-avoided">
                      {audit.usage_display}
                      <span className="font-mono text-sm text-[var(--muted)] ml-2">on {audit.platform}</span>
                    </p>
                    <p className="font-mono text-[11px] mt-2" style={{ color: "#22e58a" }} data-testid="usage-source">
                      {USAGE_SOURCE_LABEL[audit.usage_source] || "Estimated"}
                    </p>
                  </>
                ) : (
                  <p className="font-display font-bold text-2xl leading-tight text-[var(--muted)]" data-testid="usage-avoided">
                    Not enough data to estimate
                  </p>
                )}
              </div>
              <div className="md:col-span-5">
                <p className="font-mono text-[11px] tracking-[0.25em] text-neon-amber mb-2">HOW WE ESTIMATE THIS</p>
                <p className="font-mono text-xs text-[var(--muted)] leading-relaxed">
                  Rewriting lifts readiness <span className="text-white">{audit.score} → {audit.projected_score}</span>, cutting an
                  estimated <span className="text-white">{audit.est_loops_saved} correction round{audit.est_loops_saved === 1 ? "" : "s"}</span>
                  {audit.correction_complexity ? <> ({audit.correction_complexity} complexity)</> : null}.{" "}
                  {audit.usage_source ? (
                    <>Usage avoided is shown in {audit.platform}'s native unit (<span className="text-white">{audit.usage_unit === "usd" ? "USD" : audit.usage_unit}</span>) — {(USAGE_SOURCE_LABEL[audit.usage_source] || "estimated").toLowerCase()}. CoreText estimates <span className="text-white">potential</span> usage avoided, not guaranteed savings.</>
                  ) : (
                    <>{audit.platform} bills by a variable/native model, so we can't estimate usage yet. Set your observed rate in <a href="/settings" className="text-neon-cyan hover:underline">Settings</a> for a native-unit estimate.</>
                  )}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Intent Map + retention metrics */}
          {audit.intent_map && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.19 }}
              className="md:col-span-12">
              <div className="glass rounded-2xl p-6 md:p-8" data-testid="intent-map">
                <h2 className="font-display text-2xl font-bold tracking-tight text-white mb-1">Intent Map</h2>
                <p className="font-mono text-xs text-[var(--muted)] mb-6">
                  Coretext preserves your human intent while optimizing — a smaller prompt is not automatically a better prompt.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {[
                    { key: "functional_requirements", label: "FUNCTIONAL REQUIREMENTS", color: "#22d3ee" },
                    { key: "preservation_constraints", label: "PRESERVATION CONSTRAINTS", color: "#ffb000" },
                    { key: "human_intent", label: "HUMAN INTENT", color: "#22e58a" },
                  ].map((col) => (
                    <div key={col.key} className="rounded-xl p-4 bg-[#0b0d14]/60 border border-[var(--line)]" data-testid={`intent-${col.key}`}>
                      <p className="font-mono text-[10px] tracking-[0.2em] mb-3" style={{ color: col.color }}>{col.label}</p>
                      <ul className="space-y-2">
                        {(audit.intent_map[col.key] || []).length === 0 ? (
                          <li className="font-mono text-xs text-[var(--muted)]">—</li>
                        ) : (
                          audit.intent_map[col.key].map((item, i) => (
                            <li key={i} className="font-mono text-xs text-white/90 leading-relaxed flex items-start gap-2">
                              <span className="mt-1 w-1 h-1 rounded-full shrink-0" style={{ background: col.color }} />
                              {item}
                            </li>
                          ))
                        )}
                      </ul>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="retention-metrics">
                  {(() => {
                    const tr = audit.token_reduction;
                    const tokenLabel = tr == null ? "—" : tr > 0 ? `${tr}%` : tr < 0 ? `+${-tr}%` : "~0%";
                    const tokenSub = tr == null ? "" : tr > 0 ? "fewer tokens" : tr < 0 ? "added missing intent" : "same length";
                    return (
                      <Metric testid="metric-token" label="TOKEN CHANGE" value={tokenLabel} sub={tokenSub} color="#22d3ee" />
                    );
                  })()}
                  <Metric testid="metric-requirement" label="REQUIREMENT RETENTION" value={audit.requirement_retention == null ? "—" : `${audit.requirement_retention}%`} sub="functional requirements kept" color="#22e58a" />
                  <Metric testid="metric-intent" label="INTENT RETENTION" value={audit.intent_retention == null ? "—" : `${audit.intent_retention}%`} sub="human intent preserved" color="#22e58a" />
                </div>

                {audit.optimization_note && (
                  <p className="font-mono text-xs text-[var(--muted)] mt-4 leading-relaxed flex items-start gap-2" data-testid="optimization-note">
                    <Sparkles size={13} className="mt-0.5 shrink-0 text-neon-cyan" />
                    <span>
                      {audit.optimal_compression && <span className="text-neon-cyan font-bold">Optimal compression reached. </span>}
                      {audit.optimization_note}
                    </span>
                  </p>
                )}
              </div>
            </motion.div>
          )}

          {/* Improved prompt */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
            className="md:col-span-12">
            <div className="glass rounded-2xl p-6 md:p-8 relative overflow-hidden"
              style={{ border: "1px solid rgba(34,211,238,0.35)", boxShadow: "0 0 40px rgba(34,211,238,0.1)" }}>
              <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
                <h2 className="font-display text-2xl font-bold tracking-tight text-white flex items-center gap-2">
                  <Sparkles size={22} className="text-neon-cyan" /> Your tightened prompt
                </h2>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="inline-flex rounded-lg border border-neon-cyan/25 p-0.5 bg-[#070910]" data-testid="prompt-view-toggle">
                    <button
                      data-testid="view-original-tab"
                      onClick={() => setView("original")}
                      className={`inline-flex items-center gap-1.5 font-mono text-xs px-3 py-1.5 rounded-md transition-colors ${view === "original" ? "bg-neon-cyan/15 text-neon-cyan" : "text-[var(--muted)] hover:text-white"}`}
                    >
                      <FileText size={14} /> Original
                    </button>
                    <button
                      data-testid="view-prompt-tab"
                      onClick={() => setView("prompt")}
                      className={`inline-flex items-center gap-1.5 font-mono text-xs px-3 py-1.5 rounded-md transition-colors ${view === "prompt" ? "bg-neon-cyan/15 text-neon-cyan" : "text-[var(--muted)] hover:text-white"}`}
                    >
                      <Sparkles size={14} /> Improved
                    </button>
                    <button
                      data-testid="view-diff-tab"
                      onClick={() => setView("diff")}
                      className={`inline-flex items-center gap-1.5 font-mono text-xs px-3 py-1.5 rounded-md transition-colors ${view === "diff" ? "bg-neon-cyan/15 text-neon-cyan" : "text-[var(--muted)] hover:text-white"}`}
                    >
                      <GitCompareArrows size={14} /> What changed
                    </button>
                  </div>
                  <button
                    data-testid="copy-original-button"
                    onClick={copyOriginal}
                    className="rounded-lg font-display font-bold tracking-wide py-2.5 px-4 flex items-center gap-2 border border-neon-cyan/30 text-neon-cyan hover:bg-neon-cyan/10 transition-colors"
                  >
                    <Copy size={16} /> COPY ORIGINAL
                  </button>
                  <button
                    data-testid="copy-improved-button"
                    onClick={copyImproved}
                    style={{ backgroundColor: "#22d3ee", color: "#05060a" }}
                    className="rounded-lg font-display font-bold tracking-wide py-2.5 px-5 flex items-center gap-2 hover:glow-cyan hover:-translate-y-0.5 transition-transform transition-shadow duration-200"
                  >
                    <Copy size={16} /> COPY IMPROVED PROMPT
                  </button>
                </div>
              </div>
              {view === "original" ? (
                audit.original_prompt ? (
                  <pre
                    data-testid="original-prompt"
                    className="whitespace-pre-wrap font-mono text-sm leading-relaxed bg-[#070910] text-white/90 border border-[var(--line)] rounded-xl p-5 max-h-[460px] overflow-auto"
                  >
                    {audit.original_prompt}
                  </pre>
                ) : (
                  <p data-testid="original-prompt" className="font-mono text-sm text-[var(--muted)] italic bg-[#070910] border border-[var(--line)] rounded-xl p-5">
                    Original prompt was not stored for this audit.
                  </p>
                )
              ) : view === "prompt" ? (
                <pre
                  data-testid="improved-prompt"
                  className="whitespace-pre-wrap font-mono text-sm leading-relaxed bg-[#070910] text-neon-cyanBright border border-neon-cyan/25 rounded-xl p-5 max-h-[460px] overflow-auto"
                >
                  {audit.improved_prompt}
                </pre>
              ) : (
                <PromptDiff original={audit.original_prompt} improved={audit.improved_prompt} />
              )}

              <div className="mt-5 rounded-xl p-4 border border-neon-amber/40" style={{ boxShadow: "inset 0 0 24px rgba(255,122,24,0.08)" }}>
                <p className="font-mono text-[11px] tracking-[0.28em] text-neon-amber mb-2">
                  WHY THIS REDUCES REBUILD RISK
                </p>
                <p className="font-mono text-sm leading-relaxed text-white" data-testid="why-saves-credits">
                  {audit.why_saves_credits}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-8 flex items-center gap-5 flex-wrap">
          <Link
            to="/"
            data-testid="audit-another"
            style={{ backgroundColor: "#22d3ee", color: "#05060a" }}
            className="rounded-lg font-display font-bold tracking-wide py-3 px-6 inline-flex items-center gap-2 hover:glow-cyan hover:-translate-y-0.5 transition-transform transition-shadow duration-200"
          >
            AUDIT ANOTHER PROMPT
          </Link>
          <Link to="/history" className="font-mono text-sm text-neon-cyan hover:underline">
            See my score history →
          </Link>
          {audit.is_owner && (
            isPublic ? (
              <button
                data-testid="copy-share-link-button-bottom"
                onClick={copyLink}
                disabled={sharing}
                className="font-mono text-sm text-neon-cyan hover:underline inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <Share2 size={15} /> Copy share link
              </button>
            ) : (
              <button
                data-testid="share-result-button-bottom"
                onClick={enableAndCopy}
                disabled={sharing}
                className="font-mono text-sm text-[var(--muted)] hover:text-neon-cyan transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <Share2 size={15} /> Share this result
              </button>
            )
          )}
        </div>
      </main>
      <Footer />
    </Shell>
  );
}
